import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { promptDialog } from '../../components/ConfirmDialog';
import { groupCarriersByName } from '../../utils/carrierHelpers';

function AdminRequests({ token }) {
  const [requests, setRequests] = useState([]);
  const [agents, setAgents] = useState({});
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [agentsLoaded, setAgentsLoaded] = useState(false);

  // Fetch agents only once on mount (for calculating diffs)
  useEffect(() => {
    fetchAgents();
  }, []);

  // Fetch requests whenever filter changes (but only after agents are loaded)
  useEffect(() => {
    if (agentsLoaded) {
      fetchRequests();
    }
  }, [filter, agentsLoaded]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchAgents(), fetchRequests()]);
    setLoading(false);
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/agents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agentMap = {};
      response.data.forEach(agent => {
        agentMap[agent.email] = agent;
      });
      setAgents(agentMap);
      setAgentsLoaded(true);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgentsLoaded(true); // Still mark as loaded to allow requests to load
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);

      // ⭐ 'approved' filter fetches both 'approved' and 'auto_approved' from backend
      // (the backend handles this via getChangeRequestsByStatus with status='approved')
      const url = filter === 'all'
        ? '/api/admin/change-requests'
        : `/api/admin/change-requests/status/${filter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Sort by newest first
      const sorted = (response.data || []).sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      setRequests(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`/api/admin/change-requests/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Change request approved!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error approving request');
    }
  };

  const handleReject = async (id) => {
    const reason = await promptDialog('Rejection reason (optional):');
    if (reason === null) return;

    try {
      await axios.post(`/api/admin/change-requests/${id}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
      toast.success('Change request rejected!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error rejecting request');
    }
  };

  const handleReset = async (id) => {
    try {
      await axios.post(`/api/admin/change-requests/${id}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Change request reset to pending!');
    } catch (error) {
      toast.error('Error resetting request');
    }
  };

  const calculateDiff = (request) => {
    if (!request.changes.statesWithCarriers) return null;

    // ⭐ Use originalState from the request (stored when request was created)
    // This ensures diff is calculated correctly even after approve/reject
    let oldStates = request.originalState?.statesWithCarriers || {};
    const newStates = request.changes.statesWithCarriers || {};

    // ⭐ FIX: If originalState is empty, try to get current state from agents
    const isOriginalStateEmpty = !oldStates || Object.keys(oldStates).length === 0;
    if (isOriginalStateEmpty) {
      const allAgentsList = Object.values(agents);

      // Try with groupLocationId first if available
      if (request.groupLocationId) {
        const group = allAgentsList.find(a =>
          a.ghlLocationId === request.groupLocationId &&
          a.agentType === 'group'
        );

        if (group && group.groupMembers) {
          const member = group.groupMembers.find(m => m.email === request.agentEmail);
          if (member && member.statesWithCarriers) {
            oldStates = member.statesWithCarriers;
            console.log(`[DIFF] Using current member state (via groupLocationId) for ${request.agentEmail}`);
          }
        }
      }

      // If still empty, search all groups by agentEmail
      if ((!oldStates || Object.keys(oldStates).length === 0) && request.agentEmail) {
        const group = allAgentsList.find(a =>
          a.agentType === 'group' &&
          a.groupMembers &&
          a.groupMembers.some(m => m.email === request.agentEmail)
        );

        if (group && group.groupMembers) {
          const member = group.groupMembers.find(m => m.email === request.agentEmail);
          if (member && member.statesWithCarriers) {
            oldStates = member.statesWithCarriers;
            console.log(`[DIFF] Using current member state (via search) for ${request.agentEmail} in group ${group.ghlLocationName}`);
          }
        }
      }

      // If still empty, try as standalone agent
      if ((!oldStates || Object.keys(oldStates).length === 0) && request.agentEmail) {
        const standaloneAgent = agents[request.agentEmail];
        if (standaloneAgent && standaloneAgent.statesWithCarriers) {
          oldStates = standaloneAgent.statesWithCarriers;
          console.log(`[DIFF] Using current standalone agent state for ${request.agentEmail}`);
        }
      }
    }

    const diff = {
      added: [],
      removed: [],
      modified: []
    };

    // ⭐ CRITICAL FIX: Since agents can only ADD or EDIT states (not DELETE),
    // do NOT show any states as "removed" at all. Empty arrays or missing states
    // simply mean the agent didn't update that state in this request.
    // Only show added (new states) and modified (existing states with carrier changes).

    // Find added and modified states
    // Process ALL states in newStates, including those with empty arrays (modified to 0 carriers)
    Object.keys(newStates).forEach(state => {
      const newCarriersRaw = newStates[state];

      // ⭐ Empty array [] is ALWAYS an explicit deletion signal — show as removed regardless
      // of whether originalState captured this state (originalState can be stale from cache).
      if (Array.isArray(newCarriersRaw) && newCarriersRaw.length === 0) {
        diff.removed.push({ state, carriers: oldStates[state] || [] });
        return;
      }

      if (!oldStates[state]) {
        // This state is new (being added)
        diff.added.push({ state, carriers: newCarriersRaw });
      } else {
        // This state exists - check if carriers changed
        const oldCarriers = oldStates[state];
        const newCarriers = newStates[state];

        // Handle cases where carriers might be IDs (numbers) or objects
        const normalizeCarrier = (c) => {
          if (typeof c === 'number') return c;
          if (typeof c === 'object' && c !== null && (c.CarrierId || c.carrierId || c.id)) {
            return c.CarrierId || c.carrierId || c.id;
          }
          return c;
        };

        const normalizedOldCarriers = oldCarriers.map(normalizeCarrier);
        const normalizedNewCarriers = newCarriers.map(normalizeCarrier);

        const addedCarrierIds = normalizedNewCarriers.filter(nc =>
          !normalizedOldCarriers.includes(nc)
        );
        const removedCarrierIds = normalizedOldCarriers.filter(oc =>
          !normalizedNewCarriers.includes(oc)
        );

        if (addedCarrierIds.length > 0 || removedCarrierIds.length > 0) {
          // Map back to full carrier objects if available
          const addedCarriers = addedCarrierIds.map(cid => {
            const fullCarrier = newCarriers.find(c => normalizeCarrier(c) === cid);
            return fullCarrier || { CarrierId: cid };
          });
          const removedCarriers = removedCarrierIds.map(cid => {
            const fullCarrier = oldCarriers.find(c => normalizeCarrier(c) === cid);
            return fullCarrier || { CarrierId: cid };
          });

          diff.modified.push({
            state,
            added: addedCarriers,
            removed: removedCarriers
          });
        }
      }
    });

    // ⭐ NOTE: Empty array [] = explicit deletion signal, shown as "Removed State".
    // States not present in newStates were not touched by this change request.

    return diff;
  };

  // ⭐ Check if a request has state deletions (for showing warning on pending requests)
  // A state deletion is explicitly signalled by the client sending that state with an empty [] array.
  const hasStateDeletion = (request) => {
    if (!request.changes?.statesWithCarriers) return false;
    return Object.entries(request.changes.statesWithCarriers).some(([state, carriers]) => {
      return Array.isArray(carriers) && carriers.length === 0;
    });
  };

  const renderChanges = (request) => {
    if (!request || !request.changes) return <p style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>No changes specified</p>;

    // ── Enabled-toggle request ──
    if (request.changes.enabled !== undefined) {
      const wantsEnabled = request.changes.enabled;
      const currentEnabled = request.originalState?.enabled;
      return (
        <div style={{ marginTop: '12px' }}>
          <div style={{
            padding: '16px 20px', borderRadius: '8px',
            background: wantsEnabled ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${wantsEnabled ? '#059669' : '#dc2626'}`
          }}>
            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '6px', color: wantsEnabled ? '#065f46' : '#991b1b' }}>
              {wantsEnabled ? '🟢 Request to ENABLE Lead Distribution' : '🔴 Request to DISABLE Lead Distribution'}
            </div>
            <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
              <strong>Current status:</strong>{' '}
              <span style={{ fontWeight: '600', color: currentEnabled ? '#15803d' : '#dc2626' }}>
                {currentEnabled === false ? 'Disabled' : 'Enabled'}
              </span>
              {' → '}
              <span style={{ fontWeight: '700', color: wantsEnabled ? '#15803d' : '#dc2626' }}>
                {wantsEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {request.changes.toggleReason && (
              <div style={{ fontSize: '13px', background: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '10px 12px', marginTop: '8px' }}>
                <strong>Agent's reason:</strong> {request.changes.toggleReason}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Check if there are any changes at all
    const hasStateChanges = request.changes.statesWithCarriers;
    const hasConsentChanges = request.changes.consent;
    const hasLinkChanges = request.changes.healthSherpaMarketingLink !== undefined;
    const hasAnyChanges = hasStateChanges || hasConsentChanges || hasLinkChanges;
    
    if (!hasAnyChanges) {
      return <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No changes detected</div>;
    }

    return (
      <div className="request-changes" style={{ marginTop: '12px' }}>
        {request.changes.statesWithCarriers && (() => {
          const diff = calculateDiff(request);
          if (!diff) {
            return <div style={{ color: '#ef4444', fontStyle: 'italic' }}>⚠️ Unable to calculate changes (agent may have been deleted)</div>;
          }

          const hasChanges = diff.added.length > 0 || diff.removed.length > 0 || diff.modified.length > 0;
          if (!hasChanges) return null; // Don't show "No changes" here, other changes might exist

          return (
            <div style={{ marginBottom: '16px' }}>
              <strong>States & Carriers Changes:</strong>
              <div className="changes-detail" style={{ marginTop: '8px' }}>

                {/* Added States */}
                {diff.added.map(({ state, carriers }) => {
                  const grouped = groupCarriersByName(carriers);
                  return (
                    <div key={`add-${state}`} style={{
                      marginBottom: '12px',
                      padding: '12px',
                      background: '#d1fae5',
                      borderRadius: '6px',
                      border: '1px solid #059669'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#10b981' }}>+ Added State:</span> {state}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {grouped.map((group, idx) => (
                          <span key={idx} style={{
                            padding: '4px 8px',
                            background: '#a7f3d0',
                            color: '#065f46',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {group.name} ({group.carrierIds.length})
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Removed States */}
                {diff.removed.map(({ state, carriers }) => {
                  const grouped = groupCarriersByName(carriers);
                  return (
                    <div key={`remove-${state}`} style={{
                      marginBottom: '12px',
                      padding: '12px',
                      background: '#fee2e2',
                      borderRadius: '6px',
                      border: '1px solid #dc2626'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#ef4444' }}>- Removed State:</span> {state}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {grouped.map((group, idx) => (
                          <span key={idx} style={{
                            padding: '4px 8px',
                            background: '#fecaca',
                            color: '#991b1b',
                            borderRadius: '4px',
                            fontSize: '12px',
                            textDecoration: 'line-through'
                          }}>
                            {group.name} ({group.carrierIds.length})
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Modified States */}
                {diff.modified.map(({ state, added, removed }) => {
                  const addedGrouped = groupCarriersByName(added);
                  const removedGrouped = groupCarriersByName(removed);
                  return (
                    <div key={`mod-${state}`} style={{
                      marginBottom: '12px',
                      padding: '12px',
                      background: '#fef3c7',
                      borderRadius: '6px',
                      border: '1px solid #d97706'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#f59e0b' }}>~ Modified State:</span> {state}
                      </div>

                      {addedGrouped.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                            + Added Carriers:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {addedGrouped.map((group, idx) => (
                              <span key={idx} style={{
                                padding: '4px 8px',
                                background: '#d1fae5',
                                color: '#065f46',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                {group.name} ({group.carrierIds.length})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {removedGrouped.length > 0 && (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                            - Removed Carriers:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {removedGrouped.map((group, idx) => (
                              <span key={idx} style={{
                                padding: '4px 8px',
                                background: '#fee2e2',
                                color: '#991b1b',
                                borderRadius: '4px',
                                fontSize: '12px',
                                textDecoration: 'line-through'
                              }}>
                                {group.name} ({group.carrierIds.length})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {request.changes.consent && (
          <div style={{ marginBottom: '16px' }}>
            <strong>Consent Update:</strong>
            <div className="changes-preview" style={{
              marginTop: '8px',
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              maxHeight: '150px',
              overflow: 'auto',
              fontSize: '13px',
              fontFamily: 'monospace'
            }}>
              {request.changes.consent.substring(0, 200)}
              {request.changes.consent.length > 200 && '...'}
            </div>
          </div>
        )}
        {request.changes.healthSherpaMarketingLink !== undefined && (() => {
          // Get the old value from originalState or current agent
          let oldLink = request.originalState?.healthSherpaMarketingLink || '';
          
          // If originalState is empty, try to get current value
          if (!oldLink) {
            const allAgentsList = Object.values(agents);
            
            // Try with groupLocationId first
            if (request.groupLocationId) {
              const group = allAgentsList.find(a =>
                a.ghlLocationId === request.groupLocationId &&
                a.agentType === 'group'
              );
              if (group && group.groupMembers) {
                const member = group.groupMembers.find(m => m.email === request.agentEmail);
                if (member) {
                  oldLink = member.healthSherpaMarketingLink || '';
                }
              }
            }
            
            // Try standalone agent
            if (!oldLink && request.agentEmail) {
              const standaloneAgent = agents[request.agentEmail];
              if (standaloneAgent) {
                oldLink = standaloneAgent.healthSherpaMarketingLink || '';
              }
            }
          }
          
          const newLink = request.changes.healthSherpaMarketingLink || '';
          
          return (
            <div style={{ marginBottom: '16px' }}>
              <strong>🔗 HealthSherpa Marketing Link Update:</strong>
              <div style={{ marginTop: '8px' }}>
                {oldLink && (
                  <div style={{
                    padding: '8px 12px',
                    background: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #fca5a5',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                      - OLD:
                    </div>
                    <div style={{ fontSize: '13px', color: '#7f1d1d', wordBreak: 'break-all', textDecoration: 'line-through' }}>
                      {oldLink}
                    </div>
                  </div>
                )}
                {!oldLink && (
                  <div style={{
                    padding: '8px 12px',
                    background: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                      CURRENT:
                    </div>
                    <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                      No link set
                    </div>
                  </div>
                )}
                <div style={{
                  padding: '8px 12px',
                  background: '#d1fae5',
                  borderRadius: '6px',
                  border: '1px solid #6ee7b7'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                    + NEW:
                  </div>
                  <div style={{ fontSize: '13px', color: '#065f46', wordBreak: 'break-all' }}>
                    {newLink || <span style={{ fontStyle: 'italic', color: '#6b7280' }}>Link will be removed</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading change requests...</div>;

  return (
    <div className="container">
      <div className="section-header">
        <h2>Change Requests</h2>
        <div className="filter-buttons">
          <button
            className={`btn btn-sm ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`btn btn-sm ${filter === 'approved' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={`btn btn-sm ${filter === 'rejected' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
          <button
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>
      </div>

      {/* ⭐ Info banner for approved tab explaining auto-approved */}
      {filter === 'approved' && requests.length > 0 && requests.some(r => r.status === 'auto_approved') && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#1e40af',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>⚡</span>
          <span>
            <strong>Auto-Approved</strong> requests were automatically processed because they only contained state/carrier additions (no state deletions).
            Changes were immediately applied to the agent's profile.
          </span>
        </div>
      )}

      <div className="request-list">
        {requests.length === 0 ? (
          <div className="empty-state">No {filter} change requests</div>
        ) : (
          requests.map(request => (
            <div key={request.id} className="request-card" style={{
              // ⭐ Add left border color based on status
              borderLeft: request.status === 'auto_approved'
                ? '4px solid #0ea5e9'
                : request.status === 'approved'
                ? '4px solid #10b981'
                : request.status === 'pending'
                ? '4px solid #f59e0b'
                : request.status === 'rejected'
                ? '4px solid #ef4444'
                : undefined
            }}>
              <div className="request-header">
                <div>
                  <h4>
                    {request.groupLocationName ? (
                      <>
                        Group: {request.groupLocationName}
                        <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#1e40af', marginTop: '4px' }}>
                          <strong>Changes for:</strong> {request.targetMemberName || request.agentEmail}
                          {request.targetMemberEmail && ` (${request.targetMemberEmail})`}
                        </div>
                        {request.submitterName && request.submitterEmail !== request.agentEmail && (
                          <div style={{ fontSize: '13px', fontWeight: 'normal', color: '#6b7280', marginTop: '2px' }}>
                            Submitted by: {request.submitterName} ({request.submitterEmail})
                          </div>
                        )}
                      </>
                    ) : (
                      request.agentEmail
                    )}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {/* ⭐ Status badge - special styling for auto_approved */}
                    {request.status === 'auto_approved' ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '3px 10px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #7dd3fc'
                      }}>
                        ⚡ Auto-Approved
                      </span>
                    ) : (
                      <span className={`status-badge status-${request.status}`}>
                        {request.status}
                      </span>
                    )}
                    {/* ⭐ Show "State Deletion" warning badge on pending requests that have state removals */}
                    {request.status === 'pending' && hasStateDeletion(request) && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '3px 10px',
                        background: '#fef3c7',
                        color: '#92400e',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #fcd34d'
                      }}>
                        ⚠️ Contains State Removal — Needs Manual Review
                      </span>
                    )}
                  </div>
                </div>
                <div className="request-date">
                  {new Date(request.createdAt).toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>

              {renderChanges(request)}

              {/* ⭐ Show actions only for pending requests */}
              {request.status === 'pending' && (
                <div className="request-actions" style={{ marginTop: '16px' }}>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleApprove(request.id)}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleReject(request.id)}
                  >
                    Reject
                  </button>
                </div>
              )}

              {/* ⭐ Metadata section for approved/auto_approved/rejected */}
              {request.status !== 'pending' && (
                <>
                  <div className="request-meta" style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
                    {request.status === 'auto_approved' && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '10px 12px',
                        background: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #bae6fd',
                        marginBottom: '8px'
                      }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>⚡</span>
                        <div>
                          <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '2px' }}>
                            Automatically Approved by System
                          </div>
                          <div style={{ color: '#0369a1', fontSize: '12px' }}>
                            {request.autoApprovalReason || 'No state deletions detected — changes were applied instantly.'}
                          </div>
                          {request.autoApprovedAt && (
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                              Applied at: {new Date(request.autoApprovedAt).toLocaleString('en-US', {
                                timeZone: 'America/Chicago',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {request.status === 'approved' && request.approvedBy && (
                      <p>✅ Approved by: {request.approvedBy}</p>
                    )}
                    {request.status === 'rejected' && request.rejectedBy && (
                      <p>❌ Rejected by: {request.rejectedBy}</p>
                    )}
                    {request.rejectionReason && <p>Reason: {request.rejectionReason}</p>}
                  </div>
                  <div className="request-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleReset(request.id)}
                      title="Reset to pending for retesting"
                    >
                      ↺ Reset to Pending
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminRequests;
